<!-- Two SVGs, toggled by CSS media query in the component's own stylesheet
     (.flow-wide / .flow-narrow) — not a scroll wrapper. Below 640px the
     desktop layout's text would shrink illegibly (measured ~5px at 390 wide
     before this fix), so the narrow variant reflows the same five jobs
     vertically instead of shrinking the wide one. Print (the guide) is
     always comfortably wider than 640px, so it always gets the wide
     variant. A `display: none` svg drops out of the accessibility tree, so
     exactly one of these is ever visible to `getByRole('img')` /
     axe at a time. -->
<svg
  class="flow flow-wide"
  viewBox="0 0 880 380"
  role="img"
  aria-labelledby="flow-title"
  aria-describedby="flow-desc"
>
  <title id="flow-title">How sound moves in the recommended setup</title>
  <desc id="flow-desc">
    Your bass, your mic and your phone plug into the mixer. The mixer sends them
    over USB to your computer and Zoom, and Zoom sends them to Russ. Russ's
    voice and bass come back through Zoom into the mixer's computer channel and
    out to your headphones. The Record Source switch on INPUT MIX keeps Russ's
    audio out of what the mixer sends back to Russ.
  </desc>

  <!-- student sources -->
  <g class="node" data-job="bass-in">
    <rect x="20" y="30" width="150" height="48" rx="6" />
    <text x="95" y="59">Your bass</text>
  </g>
  <g class="node" data-job="voice-in">
    <rect x="20" y="110" width="150" height="48" rx="6" />
    <text x="95" y="139">Headset mic</text>
  </g>
  <g class="node" data-job="playback-in">
    <rect x="20" y="190" width="150" height="48" rx="6" />
    <text x="95" y="219">Phone (backing track)</text>
  </g>

  <!-- mixer -->
  <g class="node node-mixer">
    <rect x="270" y="30" width="180" height="208" rx="6" />
    <text x="360" y="60" class="label">Mixer</text>
    <text x="360" y="92" class="small">CH1 mic · CH2 bass</text>
    <text x="360" y="112" class="small">CH3/4 playback</text>
    <text x="360" y="160" class="small">RECORD SOURCE</text>
    <text x="360" y="180" class="small strong">INPUT MIX</text>
    <text x="360" y="218" class="small">headphone jack</text>
  </g>

  <!-- computer + zoom, russ -->
  <g class="node">
    <rect x="540" y="94" width="150" height="80" rx="6" />
    <text x="615" y="128">Your computer</text>
    <text x="615" y="150" class="small">Zoom</text>
  </g>
  <g class="node node-russ">
    <rect x="740" y="94" width="120" height="80" rx="6" />
    <text x="800" y="140" class="label">Russ</text>
  </g>

  <!-- arrows: sources into the mixer -->
  <path class="arrow" d="M170 54 H270" marker-end="url(#head)" />
  <path class="arrow" d="M170 134 H270" marker-end="url(#head)" />
  <path class="arrow" d="M170 214 H270" marker-end="url(#head)" />
  <!-- mixer to computer (USB) and on to Russ -->
  <path class="arrow" d="M450 120 H540" marker-end="url(#head)" />
  <text x="495" y="112" class="small">USB</text>
  <path class="arrow" d="M690 120 H740" marker-end="url(#head)" />
  <!-- Russ back: computer to the mixer's computer channel, out to headphones -->
  <path class="arrow arrow-back" d="M740 150 H690" marker-end="url(#head)" />
  <path class="arrow arrow-back" d="M540 150 H450" marker-end="url(#head)" />
  <g class="node" data-job="teacher-back">
    <rect x="270" y="290" width="180" height="48" rx="6" />
    <text x="360" y="319">Your headphones</text>
  </g>
  <path class="arrow arrow-back" d="M360 238 V290" marker-end="url(#head)" />

  <!-- the one rule: a dashed stub branches off the return arrow
       (M540 150 H450) and is crossed out, right where that return line
       would otherwise feed back into the send path. The label sits clear
       of the X, start-anchored so it reads left-to-right from beside it
       rather than centered on top of it. -->
  <g class="node node-rule" data-job="no-loopback">
    <path class="arrow arrow-blocked" d="M495 150 Q480 175 470 200" />
    <line class="cross" x1="458" y1="188" x2="482" y2="212" />
    <line class="cross" x1="482" y1="188" x2="458" y2="212" />
    <text x="520" y="215" class="small label-start">
      Russ's audio is not sent back to Russ
    </text>
  </g>

  <defs>
    <marker
      id="head"
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="7"
      markerHeight="7"
      orient="auto-start-reverse"
    >
      <path d="M0 0 L10 5 L0 10 z" fill="currentcolor" />
    </marker>
  </defs>
</svg>

<svg
  class="flow flow-narrow"
  viewBox="0 0 420 700"
  role="img"
  aria-labelledby="flow-title-narrow"
  aria-describedby="flow-desc-narrow"
>
  <title id="flow-title-narrow">How sound moves in the recommended setup</title>
  <desc id="flow-desc-narrow">
    Your bass, your mic and your phone plug into the mixer. The mixer sends them
    over USB to your computer and Zoom, and Zoom sends them to Russ. Russ's
    voice and bass come back through Zoom into the mixer's computer channel and
    out to your headphones. The Record Source switch on INPUT MIX keeps Russ's
    audio out of what the mixer sends back to Russ.
  </desc>

  <!-- student sources, side by side in one row (three ~120-wide boxes across
       the same 380-of-420 usable width the rest of this diagram uses) —
       each box sits directly above its own straight arrow into the mixer's
       top edge, so there is no cross-box routing to reason about (a
       stacked layout needed side-margin detours around the boxes between
       a source and the mixer; this doesn't). -->
  <g class="node" data-job="bass-in">
    <rect x="20" y="20" width="120" height="44" rx="6" />
    <text x="80" y="47">Your bass</text>
  </g>
  <g class="node" data-job="voice-in">
    <rect x="150" y="20" width="120" height="44" rx="6" />
    <text x="210" y="47">Headset mic</text>
  </g>
  <g class="node" data-job="playback-in">
    <rect x="280" y="20" width="120" height="44" rx="6" />
    <text x="340" y="47">Phone (tracks)</text>
  </g>
  <path class="arrow" d="M80 64 V100" marker-end="url(#head-narrow)" />
  <path class="arrow" d="M210 64 V100" marker-end="url(#head-narrow)" />
  <path class="arrow" d="M340 64 V100" marker-end="url(#head-narrow)" />

  <!-- mixer -->
  <g class="node node-mixer">
    <rect x="20" y="100" width="380" height="208" rx="6" />
    <text x="210" y="132" class="label">Mixer</text>
    <text x="210" y="164" class="small">CH1 mic · CH2 bass</text>
    <text x="210" y="184" class="small">CH3/4 playback</text>
    <text x="210" y="232" class="small">RECORD SOURCE</text>
    <text x="210" y="252" class="small strong">INPUT MIX</text>
    <text x="210" y="290" class="small">headphone jack</text>
  </g>

  <!-- mixer to computer (USB) and on to Russ -->
  <path class="arrow" d="M210 308 V344" marker-end="url(#head-narrow)" />
  <text x="250" y="330" class="small">USB</text>
  <g class="node">
    <rect x="20" y="344" width="380" height="70" rx="6" />
    <text x="210" y="374">Your computer</text>
    <text x="210" y="396" class="small">Zoom</text>
  </g>
  <path class="arrow" d="M210 414 V450" marker-end="url(#head-narrow)" />
  <g class="node node-russ">
    <rect x="20" y="450" width="380" height="70" rx="6" />
    <text x="210" y="491" class="label">Russ</text>
  </g>

  <!-- Russ back: a dashed line down the right margin into the headphones,
       simplified from the wide diagram's mixer-computer-channel routing —
       this is the reading-order summary, the wide diagram is the wiring
       reference. -->
  <path
    class="arrow arrow-back"
    d="M400 485 H410 V628 H400"
    marker-end="url(#head-narrow)"
  />
  <g class="node" data-job="teacher-back">
    <rect x="20" y="604" width="380" height="48" rx="6" />
    <text x="210" y="633">Your headphones</text>
  </g>

  <!-- the one rule: same shape as the wide diagram — a dashed stub off the
       return line, crossed out, label clear of the X. -->
  <g class="node node-rule" data-job="no-loopback">
    <path class="arrow arrow-blocked" d="M410 554 H370" />
    <line class="cross" x1="358" y1="542" x2="382" y2="566" />
    <line class="cross" x1="382" y1="542" x2="358" y2="566" />
    <text x="30" y="584" class="small label-start">
      Russ's audio is not sent back to Russ
    </text>
  </g>

  <defs>
    <marker
      id="head-narrow"
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="7"
      markerHeight="7"
      orient="auto-start-reverse"
    >
      <path d="M0 0 L10 5 L0 10 z" fill="currentcolor" />
    </marker>
  </defs>
</svg>
